import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import Image from '$/components/NextImage';
import styles from './index.module.scss';

interface RadishStepProps {
  icon?: string;
  title?: string;
  rate?: string;
  options?: Array<{
    key: string;
    label: string;
    active?: boolean;
    onClick?: () => void;
  }>;
  className?: string;
  showStep?: boolean;
}

type RadishStepOption = NonNullable<RadishStepProps['options']>[number];
type StepMark = {
  key: string;
  label: string;
  active: boolean;
  onClick?: () => void;
};

/**
 * 解析可能带逗号或单位的萝卜数量，供阶梯刻度和档位选择共用。
 */
function parseStepNumber(amount?: string | number) {
  if (typeof amount === 'number') return amount;
  const normalizedAmount = String(amount || '').replace(/[^\d.]/g, '');
  return Number(normalizedAmount) || 0;
}

/**
 * 将接口返回的萝卜数量转成设计稿标题里的纯数字文案。
 */
function formatStepNumber(value: number) {
  return Number.isFinite(value) ? `${Math.round(value)}` : '';
}

/**
 * 解析档位按钮中的萝卜数量，用于旗舰会员步骤条位置计算。
 */
function getOptionNumber(option: RadishStepOption) {
  return parseStepNumber(option.label) || parseStepNumber(option.key);
}

/**
 * 生成旗舰会员萝卜步骤条的档位刻度，存在后台多档时直接复用真实商品选项。
 */
function buildStepMarks(options: RadishStepOption[] = []): StepMark[] {
  const optionNumbers = options.map(getOptionNumber).filter(Boolean);
  if (optionNumbers.length <= 1) return [];
  return options.map((option, index) => ({
    key: option.key,
    label: formatStepNumber(optionNumbers[index]) || option.label,
    active: Boolean(option.active),
    onClick: option.onClick,
  }));
}

/**
 * 获取接口商品列表中当前选中的档位下标。
 */
function getActiveStepIndex(marks: StepMark[]) {
  if (!marks.length) return 0;
  const activeIndex = marks.findIndex((mark) => mark.active);
  if (activeIndex >= 0) return activeIndex;
  return 0;
}

/**
 * 渲染后台下发的萝卜 icon，字段为空时不渲染。
 */
function renderRadishIcon(icon?: string, className?: string, size = 18) {
  if (icon) {
    return <Image src={icon} width={size} height={size} alt="" className={className} />;
  }
  return null;
}

/**
 * 渲染会员卡片中的算力萝卜阶梯模块。
 */
function RadishStep(props: RadishStepProps) {
  const {
    icon = '', title = '', rate = '', options = [], className, showStep = false,
  } = props;
  const titleText = title;
  const visibleOptions = useMemo(() => options.filter((option) => option.key && option.label), [options]);
  const marks = useMemo(() => buildStepMarks(visibleOptions), [visibleOptions]);
  const activeStepIndex = getActiveStepIndex(marks);
  const activePercent = marks.length > 1 ? (activeStepIndex / (marks.length - 1)) * 100 : 0;

  /**
   * 统一处理刻度点击和拖动选择，确保父组件的商品选择逻辑只从 option 回调进入。
   */
  const handleStepChange = (nextIndex: number) => {
    marks[nextIndex]?.onClick?.();
  };

  if (!titleText && !rate) return null;

  return (
    <div className={classNames(styles['radish-step'], { [styles['step-mode']]: showStep }, className)}>
      {showStep && marks.length > 1 ? (
        <div className={styles['step-scale']}>
          <div className={styles['step-labels']}>
            {marks.map((mark, index) => (
              <button
                type="button"
                key={mark.key}
                className={classNames(styles['step-label'], { [styles['active-label']]: index === activeStepIndex })}
                onClick={() => handleStepChange(index)}
              >
                {mark.label}
                {index === activeStepIndex ? renderRadishIcon(icon, styles['label-carrot'], 14) : null}
              </button>
            ))}
          </div>
          <div
            className={styles['step-track']}
            style={{ '--radish-step-percent': `${activePercent}%` } as React.CSSProperties}
          >
            <div className={styles['step-track-active']} />
            <div className={styles['step-thumb']} />
            <input
              type="range"
              min={0}
              max={marks.length - 1}
              step={1}
              value={activeStepIndex}
              aria-label="选择算力萝卜档位"
              className={styles['step-input']}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleStepChange(Number(event.target.value))}
            />
          </div>
        </div>
      ) : null}
      <div className={styles['detail-card']}>
        {titleText ? (
          <div className={styles['detail-title']}>
            {renderRadishIcon(icon, styles['detail-carrot'])}
            {titleText}
          </div>
        ) : null}
        {rate ? <div className={styles['detail-rate']}>{rate}</div> : null}
      </div>
    </div>
  );
}

export default memo(RadishStep);
